import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/ui/loader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  FileText,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

export default function BatchReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      const response = await api().get(`/batch-reports/${id}`);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load batch report');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <PageLoader text="Loading batch report..." />;
  }

  if (!report) return null;

  const successRate = report.total_documents > 0 
    ? ((report.successful / report.total_documents) * 100).toFixed(1) 
    : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="batch-report-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/documents')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Batch Validation Report</h1>
          <p className="text-muted-foreground">Created: {formatDate(report.created_at)}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <FileText className="h-4 w-4" />
              <span className="text-sm">TOTAL DOCUMENTS</span>
            </div>
            <p className="text-3xl font-bold">{report.total_documents}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">SUCCESSFUL</span>
            </div>
            <p className="text-3xl font-bold text-emerald-700">{report.successful}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <XCircle className="h-4 w-4" />
              <span className="text-sm">FAILED</span>
            </div>
            <p className="text-3xl font-bold text-red-700">{report.failed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">SUCCESS RATE</span>
            </div>
            <p className="text-3xl font-bold">{successRate}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Results Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope']">Validation Results</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Validations</TableHead>
                <TableHead className="text-center">Passed</TableHead>
                <TableHead className="text-center">Failed</TableHead>
                <TableHead className="text-center">Issues</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.results.map((result, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <span className="font-medium">{result.document_name}</span>
                    {result.error_message && (
                      <p className="text-sm text-red-500 mt-1">{result.error_message}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      className={
                        result.status === 'success' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-red-100 text-red-700'
                      }
                    >
                      {result.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">{result.validation_count}</TableCell>
                  <TableCell className="text-center text-emerald-600 font-medium">
                    {result.passed_count}
                  </TableCell>
                  <TableCell className="text-center text-red-600 font-medium">
                    {result.failed_count}
                  </TableCell>
                  <TableCell className="text-center">
                    {result.issues_found > 0 ? (
                      <Badge variant="destructive">{result.issues_found}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Link to={`/documents/${result.document_id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/documents')}>
          Back to Documents
        </Button>
        <Button variant="outline" onClick={() => navigate('/reports')}>
          View All Reports
        </Button>
      </div>
    </div>
  );
}
